/**
 * Score History Chart — lightweight canvas line chart.
 * Reads data-history JSON from the <canvas> element and draws
 * pillar lines + total score with a tooltip on hover.
 */
(function () {
  const canvas = document.getElementById('bfwt-score-history-chart');
  if (!canvas) {
    return;
  }

  function initChart() {
    let fullHistory;
    try {
      fullHistory = JSON.parse(canvas.dataset.history || '[]');
    } catch {
      return;
    }
    if (fullHistory.length < 1) {
      return;
    }

    let history = fullHistory;

    // Extract unique quarters from history
    const quartersMap = {};
    fullHistory.forEach((pt) => {
      const d = new Date(pt.recorded_at.replace(' ', 'T'));
      const year = d.getFullYear();
      const month = d.getMonth();
      const q = Math.floor(month / 3) + 1;
      const key = `${year}-Q${q}`;
      quartersMap[key] = {
        label: `Q${q} ${year}`,
        year: year,
        quarter: q,
      };
    });

    const sortedQuarterKeys = Object.keys(quartersMap).sort((a, b) => {
      return b.localeCompare(a);
    });

    const select = document.getElementById('bfwt-score-history-filter');
    if (select) {
      select.innerHTML = '';

      const allOpt = document.createElement('option');
      allOpt.value = 'all';
      allOpt.textContent = 'All Time';
      select.appendChild(allOpt);

      sortedQuarterKeys.forEach((key) => {
        const opt = document.createElement('option');
        opt.value = key;
        opt.textContent = quartersMap[key].label;
        select.appendChild(opt);
      });

      const latestKey = sortedQuarterKeys[0];
      if (latestKey) {
        const latestQuarterPoints = fullHistory.filter((pt) => {
          const d = new Date(pt.recorded_at.replace(' ', 'T'));
          const y = d.getFullYear();
          const q = Math.floor(d.getMonth() / 3) + 1;
          return `${y}-Q${q}` === latestKey;
        });

        if (latestQuarterPoints.length >= 2) {
          select.value = latestKey;
        } else {
          select.value = 'all';
        }
      } else {
        select.value = 'all';
      }
    }

    function filterHistory(value) {
      if (value === 'all') {
        history = fullHistory;
      } else {
        const parts = value.split('-Q');
        const targetYear = parseInt(parts[0], 10);
        const targetQ = parseInt(parts[1], 10);

        history = fullHistory.filter((pt) => {
          const d = new Date(pt.recorded_at.replace(' ', 'T'));
          const y = d.getFullYear();
          const q = Math.floor(d.getMonth() / 3) + 1;
          return y === targetYear && q === targetQ;
        });
      }
    }

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Responsive sizing
    const rect = canvas.parentElement.getBoundingClientRect();
    const W = Math.round(rect.width) || 400;
    const H = 180;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    ctx.scale(dpr, dpr);

    // Layout constants
    const PAD = { top: 16, right: 12, bottom: 28, left: 32 };
    const chartW = W - PAD.left - PAD.right;
    const chartH = H - PAD.top - PAD.bottom;

    // Data bounds (always 0-10)
    const yMin = 0;
    const yMax = 10;

    function xPos(i) {
      if (history.length === 1) {
        return PAD.left + chartW / 2;
      }
      return PAD.left + (i / (history.length - 1)) * chartW;
    }
    function yPos(v) {
      return PAD.top + chartH - ((v - yMin) / (yMax - yMin)) * chartH;
    }

    // Resolve a `--color-*` triplet ("4 135 93") to a usable rgb() string.
    // Falls back to the raw CSS value (already in rgb/hex form) or a hardcoded
    // last-resort hex if the token isn't defined.
    const cs = window.getComputedStyle(document.documentElement);
    const rgbVar = (name, fallback) => {
      const raw = cs.getPropertyValue(name).trim();
      if (!raw) {
        return fallback;
      }
      return /^\d/.test(raw) ? `rgb(${raw})` : raw;
    };
    // Each pillar gets its own tier-tinted dashed line; total is the accent solid.
    const colors = {
      total: rgbVar('--color-accent', '#04875D'),
      pillar_a: '#3B82F6' /* blue — Trust */,
      pillar_b: '#F59E0B' /* amber — Value */,
      pillar_c: '#A855F7' /* violet — Demand */,
      grid: rgbVar('--color-borderline', '#E1E4E8'),
      text: rgbVar('--color-muted', '#54535E'),
    };

    function drawChart(activeIndex = -1) {
      ctx.clearRect(0, 0, W, H);

      // Horizontal grid + Y axis labels
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillStyle = colors.text;
      const ySteps = [0, 2, 4, 6, 8, 10];
      for (const v of ySteps) {
        const y = yPos(v);
        ctx.fillText(v.toString(), PAD.left - 6, y);
        ctx.beginPath();
        ctx.strokeStyle = colors.grid;
        ctx.lineWidth = 0.5;
        ctx.setLineDash([3, 3]);
        ctx.moveTo(PAD.left, y);
        ctx.lineTo(W - PAD.right, y);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // X axis date labels (first, last, and a few in between)
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = colors.text;
      const labelCount = Math.min(6, history.length);
      const step =
        history.length > 1
          ? Math.max(
              1,
              Math.floor((history.length - 1) / Math.max(1, labelCount - 1))
            )
          : 1;
      for (let i = 0; i < history.length; i += step) {
        const d = new Date(history[i].recorded_at);
        const label = d.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        });
        ctx.fillText(label, xPos(i), H - PAD.bottom + 8);
      }
      // Always show last label
      if (history.length > 1 && (history.length - 1) % step !== 0) {
        const d = new Date(history[history.length - 1].recorded_at);
        const label = d.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        });
        ctx.fillText(label, xPos(history.length - 1), H - PAD.bottom + 8);
      }

      // Draw lines
      const lines = [
        { key: 'pillar_a', color: colors.pillar_a, width: 1.2, dash: [4, 3] },
        { key: 'pillar_b', color: colors.pillar_b, width: 1.2, dash: [4, 3] },
        { key: 'pillar_c', color: colors.pillar_c, width: 1.2, dash: [4, 3] },
        { key: 'total_score', color: colors.total, width: 2.2, dash: [] },
      ];

      for (const line of lines) {
        if (history.length > 1) {
          ctx.beginPath();
          ctx.strokeStyle = line.color;
          ctx.lineWidth = line.width;
          ctx.setLineDash(line.dash);
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          for (let i = 0; i < history.length; i++) {
            const x = xPos(i);
            const y = yPos(parseFloat(history[i][line.key]));
            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      // Dots on total line
      for (let i = 0; i < history.length; i++) {
        const cx = xPos(i);
        const cy = yPos(parseFloat(history[i].total_score));
        if (i === activeIndex) {
          // Draw outer halo for hovered dot (TOC metro style premium highlight)
          ctx.beginPath();
          ctx.fillStyle = colors.total;
          ctx.globalAlpha = 0.15;
          ctx.arc(cx, cy, 9, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;

          // Draw inner white dot with thick accent stroke
          ctx.beginPath();
          ctx.fillStyle = '#FFFFFF';
          ctx.strokeStyle = colors.total;
          ctx.lineWidth = 3;
          ctx.arc(cx, cy, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        } else {
          // Draw normal dot (white fill, solid colored border like a clean metro dot)
          ctx.beginPath();
          ctx.fillStyle = '#FFFFFF';
          ctx.strokeStyle = colors.total;
          ctx.lineWidth = 2;
          ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
    }

    if (select) {
      select.addEventListener('change', function () {
        filterHistory(this.value);
        drawChart();
        hideTooltip();
      });
      filterHistory(select.value);
    }

    drawChart();

    // ── Tooltip on hover ──────────────────────────────────────────────
    let tooltipEl = null;

    function getTooltip() {
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'bfwt-score-chart-tooltip';
        canvas.parentElement.style.position = 'relative';
        canvas.parentElement.appendChild(tooltipEl);
      }
      return tooltipEl;
    }

    function hideTooltip() {
      if (tooltipEl) {
        tooltipEl.style.display = 'none';
      }
    }

    canvas.addEventListener('mousemove', function (e) {
      const r = canvas.getBoundingClientRect();
      const scaleX = W / r.width;
      const scaleY = H / r.height;
      const mx = (e.clientX - r.left) * scaleX;
      const my = (e.clientY - r.top) * scaleY;

      // Find nearest data point horizontally (X-based)
      let closest = -1;
      let minDist = Infinity;
      for (let i = 0; i < history.length; i++) {
        const dist = Math.abs(mx - xPos(i));
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      }

      // If the mouse is too far horizontally (more than 35px in screen CSS pixels) or outside vertical bounds, hide it.
      if (
        closest === -1 ||
        minDist / scaleX > 35 ||
        my < PAD.top - 10 ||
        my > H - PAD.bottom + 15
      ) {
        hideTooltip();
        drawChart(-1);
        return;
      }

      const pt = history[closest];
      const d = new Date(pt.recorded_at);
      const dateStr = d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });

      const tip = getTooltip();
      tip.innerHTML =
        '<strong>' +
        dateStr +
        '</strong>' +
        '<span>Total: ' +
        parseFloat(pt.total_score).toFixed(2) +
        '</span>' +
        '<span style="color:' +
        colors.pillar_a +
        '">Trust: ' +
        parseFloat(pt.pillar_a).toFixed(2) +
        '</span>' +
        '<span style="color:' +
        colors.pillar_b +
        '">Value: ' +
        parseFloat(pt.pillar_b).toFixed(2) +
        '</span>' +
        '<span style="color:' +
        colors.pillar_c +
        '">Popularity: ' +
        parseFloat(pt.pillar_c).toFixed(2) +
        '</span>';

      tip.style.display = 'flex';
      const tipX = xPos(closest);
      const tipY = yPos(parseFloat(pt.total_score));

      // Calculate absolute position relative to canvas's parent container
      const parentRect = canvas.parentElement.getBoundingClientRect();
      const canvasOffsetLeft = r.left - parentRect.left;
      const canvasOffsetTop = r.top - parentRect.top;

      const cssPointX = canvasOffsetLeft + tipX / scaleX;
      const cssPointY = canvasOffsetTop + tipY / scaleY;

      const tipLeft = Math.min(
        Math.max(cssPointX - 70, 0),
        parentRect.width - 150
      );

      // Position the tooltip 10px above the active dot, dynamically adjusted for height
      const tipHeight = tip.offsetHeight || 95;
      const tipTop = Math.max(8, cssPointY - tipHeight - 10);

      tip.style.left = tipLeft + 'px';
      tip.style.top = tipTop + 'px';

      // Redraw chart with active highlighted dot
      drawChart(closest);

      // Draw vertical reference line/crosshair at the bottom of the active column
      ctx.beginPath();
      ctx.strokeStyle = colors.grid;
      ctx.lineWidth = 0.8;
      ctx.setLineDash([2, 2]);
      ctx.moveTo(xPos(closest), PAD.top);
      ctx.lineTo(xPos(closest), H - PAD.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
    });

    canvas.addEventListener('mouseleave', function () {
      hideTooltip();
      drawChart(-1);
    });
  }

  // Defer initialization using requestAnimationFrame on ready state
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      requestAnimationFrame(initChart);
    });
  } else {
    requestAnimationFrame(initChart);
  }
})();
